/**
 * Privacy guard for client PostHog events (CLAUDE.md §5). Pure module (no RN
 * imports) so it is unit-testable.
 *
 * Events carry ids, counts, enums and durations only. A property on the
 * forbidden list is dropped in `captureEvent` whatever the call site passed,
 * so a careless `file_name: file.name` or `error_message: err.message` never
 * reaches PostHog. Mirrors FORBIDDEN_PROPERTY_KEYS in
 * backend/app/services/product_analytics.py.
 */

export const FORBIDDEN_EVENT_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  'email',
  'name',
  'full_name',
  'first_name',
  'last_name',
  'phone',
  'content',
  'text',
  'transcript',
  'message',
  'prompt',
  'response',
  'title',
  'session_title',
  'document_name',
  'filename',
  'file_name',
  'school_name',
  'error_message',
  'error_preview',
  'token',
  'password',
]);

/** Top-level properties minus the forbidden keys (case-insensitive). */
export function sanitizeEventProperties<T extends Record<string, unknown>>(
  properties: T | undefined,
): Record<string, unknown> {
  if (!properties) return {};
  const out: Record<string, unknown> = {};
  Object.entries(properties).forEach(([key, value]) => {
    if (FORBIDDEN_EVENT_PROPERTY_KEYS.has(key.toLowerCase())) return;
    out[key] = value;
  });
  return out;
}

type ErrorLike = {
  status?: unknown;
  code?: unknown;
  detail?: unknown;
  response?: { status?: unknown; data?: { detail?: unknown } };
} | null | undefined;

const STABLE_CODE = /^[a-z][a-z0-9_]{1,63}$/;

/**
 * A stable, enum-like `error_code` for an analytics event, never free text:
 * the backend's structured `detail.code` (e.g. `magic_link_invalid`,
 * `document_has_no_transcript`), else a job error code (`provider_unavailable`),
 * else `http_<status>`, else `network_error` / `unknown`.
 */
export function analyticsErrorCode(error: unknown): string {
  const e = error as ErrorLike;
  const detail = e?.response?.data?.detail ?? e?.detail;
  if (detail && typeof detail === 'object') {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === 'string' && STABLE_CODE.test(code)) return code;
  }
  if (typeof e?.code === 'string' && STABLE_CODE.test(e.code)) return e.code;
  const status = e?.response?.status ?? e?.status;
  if (typeof status === 'number' && status > 0) return `http_${status}`;
  if (error instanceof Error && /network|fetch|timeout/i.test(error.message)) return 'network_error';
  return 'unknown';
}

/** The lower-cased extension of a file name (`m4a`), never the name itself. */
export function fileExtensionOf(fileName: string | null | undefined): string | undefined {
  if (!fileName) return undefined;
  const match = /\.([a-z0-9]{1,8})$/i.exec(fileName.trim());
  return match ? match[1].toLowerCase() : undefined;
}
